import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import { TableRow } from '@material-ui/core';
import { StyledTableCellProps as StyledTableCell, StyledTooltip } from '../../commons/utils/TableStyles';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';

/**
 * VEC-3313 Scenarios 02 y 03 — fila de la grilla de Stock por Depósito.
 *
 * Las dos alertas NO se recalculan acá: llegan resueltas en `row.estadoAlerta`, que el backend
 * deriva en un solo lugar (`ItemStockRepository::calcularEstadoAlerta`, con su espejo en SQL para
 * el filtro). Duplicar la regla en el front es exactamente lo que hoy pasa en el catálogo de
 * VEC-3312 —`estaBajoMinimo()` en InventarioItemsGridRow repite el `<` del backend— y es por lo que
 * si allá se cambia el umbral hay que acordarse de tocar los dos lados.
 *
 * Los tres estados y su lectura:
 *   SIN_STOCK   disponible en 0. Gana sobre STOCK_BAJO, que es el caso más grave (Sc. 03).
 *   STOCK_BAJO  disponible <= mínimo, con mínimo configurado y mayor a cero (Sc. 02).
 *   OK          todo lo demás, y también el ítem sin mínimo definido: sin mínimo no hay alerta.
 */
const ALERTA_SIN_STOCK = 'SIN_STOCK';
const ALERTA_STOCK_BAJO = 'STOCK_BAJO';

const InventarioStockGridRow = (props) => {
  const intl = props.intl;
  const row = props.row;
  const [redirectTo, setRedirectTo] = useState(null);

  /** Los conteos de stock son ENVASES: enteros, sin decimales. El decimal vive en el factor de la unidad. */
  const formatEntero = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return LocaleFormat.formatNumber(Number(value), 0);
  };

  /** El mínimo sí es decimal (`items.stock_minimo`). Vacío = sin mínimo configurado = no se controla. */
  const formatMinimo = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return LocaleFormat.formatNumber(Number(value), 2);
  };

  const esSinStock = row.estadoAlerta === ALERTA_SIN_STOCK;
  const esStockBajo = row.estadoAlerta === ALERTA_STOCK_BAJO;

  /**
   * El estado se muestra como badge de texto y no solo como color: un semáforo sin etiqueta es
   * inaccesible para quien no distingue rojo de amarillo, y la card pide una "alerta", que es algo
   * que se tiene que poder leer.
   */
  const renderEstado = () => {
    if (esSinStock) {
      return (
        <span className="badge badge-danger">
          {intl.formatMessage({ id: 'inventarioStock.grid.alerta_sin_stock', defaultMessage: 'Sin stock' })}
        </span>
      );
    }

    if (esStockBajo) {
      return (
        <StyledTooltip
          title={intl.formatMessage(
            {
              id: 'inventarioStock.grid.alerta_stock_bajo_detalle',
              defaultMessage: 'Disponible {disponible}, mínimo {minimo}',
            },
            { disponible: formatEntero(row.stockDisponible), minimo: formatMinimo(row.stockMinimo) }
          )}
          arrow
          placement="left"
        >
          <span className="badge badge-warning">
            {intl.formatMessage({ id: 'inventarioStock.grid.alerta_stock_bajo', defaultMessage: 'Stock bajo' })}
          </span>
        </StyledTooltip>
      );
    }

    return (
      <span className="badge badge-success">
        {intl.formatMessage({ id: 'inventarioStock.grid.alerta_ok', defaultMessage: 'OK' })}
      </span>
    );
  };

  return (
    <>
      {redirectTo && <Redirect push to={redirectTo} />}
      <TableRow>
        <StyledTableCell align="left">{row.numeroParte || '-'}</StyledTableCell>
        <StyledTableCell align="left">{row.descripcion}</StyledTableCell>
        <StyledTableCell align="left">{row.categoria || '-'}</StyledTableCell>
        <StyledTableCell align="left">{row.subcategoria || '-'}</StyledTableCell>
        <StyledTableCell align="left">{row.marca || '-'}</StyledTableCell>
        <StyledTableCell align="left">{row.unidadAbreviatura || row.unidad || '-'}</StyledTableCell>
        <StyledTableCell align="right">{formatEntero(row.stockFisico)}</StyledTableCell>
        <StyledTableCell align="right">{formatEntero(row.stockReservado)}</StyledTableCell>
        <StyledTableCell align="right">
          <span className={esSinStock || esStockBajo ? 'text-danger' : ''} style={{ fontWeight: 600 }}>
            {formatEntero(row.stockDisponible)}
          </span>
        </StyledTableCell>
        <StyledTableCell align="right">{formatMinimo(row.stockMinimo)}</StyledTableCell>
        <StyledTableCell align="center">{renderEstado()}</StyledTableCell>
        <StyledTableCell>
          <div className="d-inline-flex">
            {/* Scenario 04 — ingreso rápido CON EL ÍTEM PRESELECCIONADO. El destino es el formulario
                de VEC-3314, que lee el ítem del query string (InventarioStockIngresoAbm). Va por
                query string y no por la ruta porque es una preselección, no la identidad de la
                pantalla. Es la misma acción que el catálogo ofrece como "Cargar stock". */}
            {props.permissions.ingresar && (
              <StyledTooltip
                title={intl.formatMessage({
                  id: 'inventarioStock.grid.ingreso_rapido',
                  defaultMessage: 'Registrar ingreso de este ítem',
                })}
                arrow
                placement="top"
              >
                <button
                  className="action btn btn-sm btn-icon btn-dt-grid text-success"
                  onClick={() =>
                    setRedirectTo(props.baseUrl + '/ingresos/nuevo?item=' + row.id + '&origen=stock')
                  }
                >
                  <i className="fa fa-plus fa-xs" />
                </button>
              </StyledTooltip>
            )}
            {/* Scenario 05 — historial del ítem EN EL DEPÓSITO SELECCIONADO. La ubicación vigente no
                se pasa acá: la sabe la grilla, que es quien tiene el filtro aplicado, y se la da al
                modal. Así la fila no necesita conocer el estado del panel de filtros. */}
            {props.permissions.verMovimientos && (
              <StyledTooltip
                title={intl.formatMessage({
                  id: 'inventarioStock.grid.ver_movimientos',
                  defaultMessage: 'Historial de movimientos',
                })}
                arrow
                placement="top"
              >
                <button
                  className="action btn btn-sm btn-icon btn-dt-grid text-fleet"
                  onClick={() => props.onVerMovimientos(row)}
                >
                  <i className="la la-history fa-xs" />
                </button>
              </StyledTooltip>
            )}
          </div>
        </StyledTableCell>
      </TableRow>
    </>
  );
};

export default InventarioStockGridRow;
